import { EmbeddableWorkbookDataQualityWarning } from '@tableau/data-catalog-client-components';
import { LabelAssetType } from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ISanitizer } from '~commonInfra/utils/Sanitize';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { SiteUrlsProps } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { LabelWrapper } from '~src/catalog/CatalogLabelsHookHelper';
import { getTelemetryCommonProperties } from '~src/catalog/DataCatalogTelemetry';
import { useSelector } from '~src/settings/general-settings/utils';
import { ItemDescription } from '~src/spacesPlatform/components/header/HeaderItemDescription';
import { ModifiedDateMetadata, OwnerMetadata } from '~src/spacesPlatform/components/header/HeaderMetadataRow';
import { ActionsMenuIcon, DetailButtonIcon, FavoriteToggleWithCountsIcon } from '~src/spacesPlatform/components/header/HeaderNameRowIcons';
import { ConnectedSpaceHeader } from '~src/spacesPlatform/components/header/SpaceHeader';
import { SpaceHeaderStyles } from '~src/spacesPlatform/components/header/SpaceHeaderStyles';
import { ExplorableItemType } from '~src/spacesPlatform/models/ContentType';
import { IContentInfo } from '~src/spacesPlatform/models/IContentInfo';

import { CatalogApiRequests } from '../../catalog/CatalogApiRequests';
import { catalogApolloClient } from '../../catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '../../catalog/CatalogFeatureFlagsAdapter';
import { DataCatalogUIActionsService } from '../../catalog/services/DataCatalogUIActionsService';
import { DraftWorkbookSpaceProps } from '../../drafts/types/DraftsTypes';
import { ConnectedExtractMetadata } from '../../extractTask/components/spaces/header/ExtractMetadata';
import { ConnectedExtractTasksMetadata } from '../../extractTask/components/spaces/header/ExtractTasksMetadata';
import { ISpaceItemDetailInfo } from '../../spacesPlatform/models/ISpaceItemDetailInfo';
import { EditWorkbookAction } from '../entityActions/EditWorkbookAction';
import { IWorkbookActions } from '../ServerApi/types/IWorkbookActions';
import { WorkbookSpaceHeaderDraftBanner } from './WorkbookSpaceHeaderDraftBanner';

export interface WorkbookSpaceHeaderProps extends SiteUrlsProps {
  readonly item: ISpaceItemDetailInfo<'workbook'>;
  readonly availableActions: IWorkbookActions;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly sanitizer: ISanitizer;
  readonly catalogingOnSiteEnabled: boolean;
  readonly draft?: DraftWorkbookSpaceProps;
}

export function WorkbookSpaceHeader(props: WorkbookSpaceHeaderProps): JSX.Element {

  const itemInfo = props.item as unknown as IContentInfo<Extract<ExplorableItemType, LabelAssetType>>;  return <ConnectedSpaceHeader
    item={props.item}
    availableActions={props.availableActions}
    nameRowIcons={
      <React.Fragment>
        {FavoriteToggleWithCountsIcon({
          item: props.item,
          addToFavorites: props.addToFavorites,
          removeFromFavorites: props.removeFromFavorites,
        })}
        {DetailButtonIcon(props.item, props.openDetail)}
        {ActionsMenuIcon(props.item)}
      </React.Fragment>
    }
    metadataRow={
      <React.Fragment>
        {OwnerMetadata(props.item.owner, props.urls)}
        {ModifiedDateMetadata(props.item.updatedAt)}
        <ConnectedExtractMetadata itemInfo={props.item} availableActions={props.availableActions} />
        <ConnectedExtractTasksMetadata itemInfo={props.item} availableActions={props.availableActions} />
        {props.catalogingOnSiteEnabled ?
          (FeatureFlags.labelsInMorePlacesEnabled ?
            <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
              <LabelWrapper itemInfo={itemInfo} category={'warning'} masthead={true} kind={'chip'}/>
            </span>
            : DataQualityMetadata(props.item, props.urls)) : null}
      </React.Fragment>
    }
    descriptionElement={ItemDescription(props.item, props.openDetail, props.sanitizer)}
    spotlightActions={props.draft ? [] : [EditWorkbookAction]}
    bottomElement={FeatureFlags.webAuthoringAutoSaveEnabled && props.draft
      ? <WorkbookSpaceHeaderDraftBanner workbookContent={props.item} draftInfo={props.draft} />
      : null}
  />;
}

/** @deprecated TFS1478812 */
function DataQualityMetadata(
  workbookInfo: ISpaceItemDetailInfo<'workbook'>,
  urls: SiteUrls,
): JSX.Element | null {
  const siteLuid = useSelector(SessionSiteSelectors.siteLuid);

  return (
    <>
      <span className={SpaceHeaderStyles.TextOnlyMetadataLabelWithLeftMargin}>
        <EmbeddableWorkbookDataQualityWarning
          client={catalogApolloClient}
          vizportalUrlId={workbookInfo.id}
          siteUrls={urls}
          apiRequests={CatalogApiRequests}
          uiActions={DataCatalogUIActionsService.instance()}
          featureFlags={catalogFeatureFlagsAdapter()}
          telemetryContext={getTelemetryCommonProperties()}
          source={'WorkbookSpaceHeader'}
          siteLuid={siteLuid}
        />
      </span>
    </>
  );
}
